import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import ArticleCard from '../../components/ArticleCard/ArticleCard';
import { getArticlesByCategory } from '../../utils/articles';
import { FiChevronRight } from '../../components/Icons';

const CategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const articles = getArticlesByCategory(category || '');

  const categoryInfo: { [key: string]: { title: string; description: string; icon: string } } = {
    'getting-started': {
      title: 'Getting Started',
      description: 'Everything you need to know to start using your CRM effectively',
      icon: '🚀'
    },
    'customers': {
      title: 'Customer Management',
      description: 'Learn how to manage your customer database, import contacts, and track relationships',
      icon: '👥'
    },
    'appointments': {
      title: 'Appointments & Scheduling',
      description: 'Master appointment booking, calendar management, and scheduling optimization',
      icon: '📅'
    },
    'finances': {
      title: 'Financial Management',
      description: 'Create invoices, manage estimates, process payments, and track your finances',
      icon: '💰'
    },
    'marketing': {
      title: 'Marketing & Leads',
      description: 'Capture leads, run campaigns, and grow your business with marketing tools',
      icon: '📢'
    },
    'services': {
      title: 'Service Management',
      description: 'Configure services, set pricing, and use estimator tools',
      icon: '🛠️'
    },
    'orders': {
      title: 'Orders & Inventory',
      description: 'Process orders, track inventory, and manage your supply chain',
      icon: '📦'
    },
    'documents': {
      title: 'Documents & Reports',
      description: 'Generate documents, create reports, and manage your business paperwork',
      icon: '📄'
    },
    'admin': {
      title: 'Admin & Settings',
      description: 'Manage employees, configure settings, and control system access',
      icon: '⚙️'
    },
    'tools': {
      title: 'Tools & Features',
      description: 'Todo lists, daily notes, and other productivity tools',
      icon: '🔧'
    }
  };

  const info = categoryInfo[category || ''] || {
    title: category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Category',
    description: 'Browse articles in this category',
    icon: '📚'
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-gray-700">Home</Link>
          <FiChevronRight className="h-4 w-4" />
          <span className="text-gray-900">{info.title}</span>
        </nav>

        {/* Category Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-start space-x-4">
            <div className="text-4xl">{info.icon}</div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                {info.title}
              </h1>
              <p className="text-lg text-gray-600">
                {info.description}
              </p>
            </div>
          </div>
        </div>

        {/* Articles Grid */}
        {articles.length > 0 ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Articles ({articles.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              No articles found in this category yet.
            </p>
            <Link
              to="/"
              className="inline-block mt-4 text-blue-600 hover:text-blue-800"
            >
              Browse all categories
            </Link>
          </div>
        )}

        {/* Related Categories */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Browse Other Categories
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(categoryInfo)
              .filter(([key]) => key !== category)
              .slice(0, 4)
              .map(([key, value]) => (
                <Link
                  key={key}
                  to={`/categories/${key}`}
                  className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="text-2xl">{value.icon}</span>
                  <span className="text-sm font-medium text-gray-700">
                    {value.title}
                  </span>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CategoryPage;